/**
 * Evaluation of checked programs (W1-10c core-expr): scope reads, seeded random draws, unit conversions made
 * explicit by the checker, and the assignment operators.
 *
 * Strictness: every intermediate value must be a finite number. Division or remainder by zero, a function
 * outside its domain (`sqrt(-1)`, `log(0)`, `asin(2)`), overflow to infinity and missing item values are
 * errors carrying the source range of the offending part, so the UI underlines exactly what failed.
 *
 * Scope fallbacks: when a program reads `s` (seconds) and the item only has `t` (beat), or the other way round,
 * the value is converted with the tempo map passed to `apply` (Phira's beat <-> seconds).
 */

import type { Rng } from '../math/types';
import type { TempoMap } from '../time/types';
import type { Result } from '../util/types';
import { EXPR_MSG, ExprFailure, fail, type Span } from './errors';
import { FUNCTIONS, type FnContext } from './functions';
import { SPEED_UNIT_PX_PER_SEC, type Ir, type ScopeKey } from './ir';
import type { AssignOp, ExprError, ExprScope } from './types';

export interface EvalEnv {
  readonly scope: ExprScope;
  readonly rng: Rng;
  readonly tempo: TempoMap | undefined;
}

function finite(x: number, span: Span, nanKey: string, params?: Readonly<Record<string, string | number>>): number {
  if (Number.isFinite(x)) return x;
  if (Number.isNaN(x)) fail(nanKey, span, params);
  return fail(EXPR_MSG.tooLarge, span);
}

function readScope(key: ScopeKey, env: EvalEnv): number | undefined {
  const s = env.scope;
  switch (key) {
    case 'v':
      return s.v;
    case 'i':
      return s.i;
    case 'n':
      return s.n;
    case 'u':
      return s.u;
    case 't':
      if (s.t !== undefined) return s.t;
      return s.s !== undefined && env.tempo !== undefined ? env.tempo.beatF64At(s.s) : undefined;
    case 's':
      if (s.s !== undefined) return s.s;
      return s.t !== undefined && env.tempo !== undefined ? env.tempo.secAtBeatF64(s.t) : undefined;
    case 'x':
      return s.x;
    case 'speed':
      return s.speed;
    case 'size':
      return s.size;
    case 'v0':
      return s.v0;
    case 'v1':
      return s.v1;
    case 'len':
      return s.len;
  }
}

/** Floored remainder: the result has the divisor's sign (-1 % 3 = 2). */
function floorMod(a: number, b: number): number {
  const r = a % b;
  return r !== 0 && r < 0 !== b < 0 ? r + b : r;
}

export function evaluate(node: Ir, env: EvalEnv): number {
  switch (node.op) {
    case 'num':
      return node.value;
    case 'var': {
      const v = readScope(node.key, env);
      if (v === undefined || !Number.isFinite(v)) fail(EXPR_MSG.missingValue, node, { name: node.name });
      return v;
    }
    case 'vel': {
      const dur = env.scope.durSec;
      if (dur === undefined || !Number.isFinite(dur)) fail(EXPR_MSG.needsSegment, node);
      return finite(SPEED_UNIT_PX_PER_SEC * dur * node.value, node, EXPR_MSG.notFinite);
    }
    case 'segStart': {
      const v0 = env.scope.v0;
      if (v0 === undefined || !Number.isFinite(v0)) fail(EXPR_MSG.needsSegment, node);
      return v0;
    }
    case 'neg':
      return -evaluate(node.arg, env);
    case 'scale':
      return finite(evaluate(node.arg, env) * node.factor, node, EXPR_MSG.notFinite);
    case 'bin':
      return binary(node, evaluate(node.a, env), evaluate(node.b, env));
    case 'call': {
      if (node.fn === 'alt') {
        const k = node.args.length;
        const i = env.scope.i;
        if (!Number.isFinite(i)) fail(EXPR_MSG.missingValue, node, { name: 'i' });
        const pick = node.args[floorMod(Math.trunc(i), k)];
        if (pick === undefined) return fail(EXPR_MSG.notFinite, node);
        return evaluate(pick, env);
      }
      const spec = FUNCTIONS[node.fn];
      const args = node.args.map((a) => evaluate(a, env));
      const ctx: FnContext = {
        rng: env.rng,
        tempo: env.tempo,
        fail: (key, params) => fail(key, node, params),
      };
      const impl = spec.impl;
      if (impl === null) return fail(EXPR_MSG.notFinite, node);
      return finite(impl(args, ctx), node, EXPR_MSG.undefinedFn, { name: spec.name });
    }
  }
}

function binary(node: Extract<Ir, { op: 'bin' }>, a: number, b: number): number {
  switch (node.fn) {
    case '+':
      return finite(a + b, node, EXPR_MSG.notFinite);
    case '-':
      return finite(a - b, node, EXPR_MSG.notFinite);
    case '*':
      return finite(a * b, node, EXPR_MSG.notFinite);
    case '/':
      if (b === 0) fail(EXPR_MSG.divByZero, node.b);
      return finite(a / b, node, EXPR_MSG.notFinite);
    case '%':
      if (b === 0) fail(EXPR_MSG.modByZero, node.b);
      return finite(floorMod(a, b), node, EXPR_MSG.notFinite);
    case '^':
      return finite(a ** b, node, EXPR_MSG.undefinedPow);
  }
}

/** Applies an assignment operator to the item's current value `v`. */
export function assignValue(assign: AssignOp, rhs: number, env: EvalEnv, span: Span): number {
  if (assign === '=') return rhs;
  const v = env.scope.v;
  if (!Number.isFinite(v)) fail(EXPR_MSG.missingValue, span, { name: 'v' });
  switch (assign) {
    case '+=':
      return finite(v + rhs, span, EXPR_MSG.notFinite);
    case '-=':
      return finite(v - rhs, span, EXPR_MSG.notFinite);
    case '*=':
      return finite(v * rhs, span, EXPR_MSG.notFinite);
    case '/=':
      if (rhs === 0) fail(EXPR_MSG.divByZero, span);
      return finite(v / rhs, span, EXPR_MSG.notFinite);
  }
}

/** Evaluates `rhs` and applies `assign`; -0 is returned as 0. */
export function run(rhs: Ir, assign: AssignOp, env: EvalEnv, span: Span): Result<number, ExprError> {
  try {
    const out = assignValue(assign, evaluate(rhs, env), env, span);
    return { ok: true, value: out === 0 ? 0 : out };
  } catch (e) {
    if (e instanceof ExprFailure) return { ok: false, error: e.error };
    throw e;
  }
}
